import { clsx } from "clsx";
import { isAfter, isBefore, isFuture, isSameDay, isToday } from "date-fns";

import {
  dayClassNames,
  dayWrapperClassNames,
  disabledDayClassNames,
  selectedDayClassNames,
  todayClassNames,
} from "./class-names";
import { weekdaysShort } from "./types";

type DayPickerProps = {
  year: number;
  month: number;
  onDateChange: (date: Date) => void;
  startsOnSunday?: boolean;
  selectedDate?: Date;
  disabledDays?: {
    before?: Date;
    after?: Date;
  };
};
export const DayPicker = ({
  year,
  month,
  onDateChange,
  selectedDate,
  disabledDays,
  startsOnSunday = false,
}: DayPickerProps) => {
  const daysOfWeek = startsOnSunday
    ? [...weekdaysShort.slice(6), ...weekdaysShort.slice(0, 6)]
    : weekdaysShort;

  const firstDayIndex = new Date(year, month, 1).getDay();
  const totalDays = new Date(year, month + 1, 0).getDate();

  const calendar: (number | null)[][] = [];
  let week: (number | null)[] = [];

  const startOffset = startsOnSunday ? firstDayIndex : (firstDayIndex + 6) % 7;
  for (let i = 0; i < startOffset; i += 1) {
    week.push(null);
  }

  for (let day = 1; day <= totalDays; day += 1) {
    week.push(day);
    if (week.length === 7) {
      calendar.push(week);
      week = [];
    }
  }

  if (week.length) {
    while (week.length < 7) {
      week.push(null);
    }
    calendar.push(week);
  }

  // fixed height so the calendar doesn't flip or jump between months
  while (calendar.length < 6) {
    calendar.push(Array(7).fill(null));
  }

  return (
    <div className="flex flex-col w-full gap-small">
      <div
        className={clsx(dayWrapperClassNames, "font-semibold text-neutral-600")}
      >
        {daysOfWeek.map((d) => (
          <div className={dayClassNames} key={d}>
            {d}
          </div>
        ))}
      </div>
      <div className={clsx(dayWrapperClassNames, "flex-wrap")}>
        {calendar.flat().map((d, idx) => {
          const key = `${d}-${idx}`;
          const cellDate = d ? new Date(year, month, d) : null;
          const isFutureDay = cellDate ? isFuture(cellDate) : true;
          const isBeforeDisabled =
            cellDate && disabledDays?.before
              ? isBefore(cellDate, disabledDays.before)
              : false;
          const isAfterDisabled =
            cellDate && disabledDays?.after
              ? isAfter(cellDate, disabledDays.after)
              : false;
          const disabled = isFutureDay || isBeforeDisabled || isAfterDisabled;
          const today = cellDate ? isToday(cellDate) : false;
          const isSelected =
            cellDate && selectedDate
              ? isSameDay(cellDate, selectedDate)
              : false;

          return d ? (
            <button
              disabled={disabled}
              type="button"
              key={key}
              onClick={(e) => {
                if (cellDate) onDateChange(cellDate);
                e.stopPropagation();
                e.preventDefault();
              }}
              className={clsx(
                dayClassNames,
                today && todayClassNames,
                disabled && disabledDayClassNames,
                isSelected && selectedDayClassNames,
              )}
            >
              {d}
            </button>
          ) : (
            <div key={key} className={dayClassNames} />
          );
        })}
      </div>
    </div>
  );
};
